import * as React from 'react';
import { cn } from '@noor/lib/cn';

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        // Metrics resolve through `--card-*` sizing handles.
        'flex flex-col gap-[var(--card-gap)] rounded-[var(--card-radius)] border border-card-border/70 bg-card-bg py-[var(--card-padding-y)] text-card-fg shadow-sm transition-[box-shadow,border-color] duration-200 hover:border-card-border',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Card header. Pass `divided` for the enterprise section-card look — a hairline
 * rule between the header and the body (use for content cards like Details /
 * Timeline; omit on stat/metric cards where a divider would split the label
 * from its value).
 */
function CardHeader({
  className,
  divided = false,
  ...props
}: React.ComponentProps<'div'> & { divided?: boolean }) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-[var(--card-padding-x)] has-data-[slot=card-action]:grid-cols-[1fr_auto]',
        divided && 'border-b border-border/60 pb-4',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-[length:var(--card-title-font-size)] leading-tight font-[var(--card-title-font-weight)] tracking-tight',
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        'text-[length:var(--card-description-font-size)] leading-[var(--card-description-line-height)] text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-[var(--card-padding-x)]', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex items-center px-[var(--card-padding-x)] [.border-t]:pt-5',
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
